import type { OperatingSystemPreviewDescriptor } from "./operating-systems";

export type OperatingSystemCachedPreviewEntry = OperatingSystemPreviewDescriptor & {
  createdAt: number;
  lastUsedAt: number;
  loadedAt: number | null;
  reloadNonce: number;
  candidateForCacheKey?: string | null;
  /**
   * The most recently normalized in-memory document. This deliberately never
   * reaches browser storage: it only lets an already-loaded frame remount
   * without flashing blank while its Provider proxy wakes back up.
   */
  lastSuccessfulSrcDoc?: string | null;
};

export type OperatingSystemRuntimeLifecycleClientSignal =
  | "runtime_loaded"
  | "candidate_rejected"
  | "runtime_error";

export type OperatingSystemRuntimeLifecycleClientReason =
  | "runtime_ready"
  | "provider_loading"
  | "route_not_found"
  | "runtime_error";

export type OperatingSystemRuntimeLifecycleClientPhase =
  | "before_ready"
  | "after_ready"
  | "resource_error"
  | "script_error"
  | "unhandled_rejection";

export type OperatingSystemRuntimeLifecycleEventPayload = {
  operatingSystemId: string;
  signal: OperatingSystemRuntimeLifecycleClientSignal;
  runtimeSource: "cache" | "app";
  version: 1;
  cacheKey: string;
  reason: OperatingSystemRuntimeLifecycleClientReason;
  phase?: OperatingSystemRuntimeLifecycleClientPhase;
};

export function canonicalPreviewCacheKey(
  entry: Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">,
): string {
  return entry.candidateForCacheKey || entry.cacheKey;
}

export function isPendingPreviewCandidate(
  entry: Pick<OperatingSystemCachedPreviewEntry, "candidateForCacheKey" | "loadedAt">,
): boolean {
  return !!entry.candidateForCacheKey && !entry.loadedAt;
}

export function shouldKeepInFlightPreview(
  entry: Pick<OperatingSystemCachedPreviewEntry, "url" | "loadedAt"> | null | undefined,
  nextUrl: string,
  force = false,
): boolean {
  return !force && !!entry && !entry.loadedAt && entry.url === nextUrl;
}

export function shouldPromotePreviewCandidate(
  entry: Pick<OperatingSystemCachedPreviewEntry, "candidateForCacheKey" | "loadedAt">,
  receivedStableSignal: boolean,
): boolean {
  return receivedStableSignal && isPendingPreviewCandidate(entry);
}

export function previewEntryBelongsToCacheKey(
  entry: Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">,
  cacheKey: string,
): boolean {
  return canonicalPreviewCacheKey(entry) === cacheKey;
}

export function previewEntrySharesFamilyWithEntryKey(
  entries: Array<Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">>,
  entry: Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">,
  entryKey: string | null,
): boolean {
  if (!entryKey) return false;
  const keyedEntry = entries.find((candidate) => candidate.cacheKey === entryKey);
  const canonicalEntryKey = keyedEntry ? canonicalPreviewCacheKey(keyedEntry) : entryKey;
  return entry.cacheKey === entryKey || canonicalPreviewCacheKey(entry) === canonicalEntryKey;
}

export function findExistingPreviewEntry(
  entries: OperatingSystemCachedPreviewEntry[],
  cacheKey: string,
): OperatingSystemCachedPreviewEntry | undefined {
  const exactEntry = entries.find((entry) => entry.cacheKey === cacheKey);
  if (exactEntry && shouldRevealPreviewFrame(exactEntry)) return exactEntry;

  const loadedFamilyEntry = entries
    .filter((entry) => previewEntryBelongsToCacheKey(entry, cacheKey) && shouldRevealPreviewFrame(entry))
    .sort((first, second) => second.lastUsedAt - first.lastUsedAt)[0];
  return loadedFamilyEntry ?? exactEntry;
}

export function isActivePreviewEntryLoaded(
  entries: Array<Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey" | "loadedAt" | "lastSuccessfulSrcDoc">>,
  activeCacheKey: string | null,
  descriptorCacheKey: string,
): boolean {
  if (!activeCacheKey) return false;
  const activeEntry = entries.find((entry) => entry.cacheKey === activeCacheKey);
  return !!activeEntry
    && shouldRevealPreviewFrame(activeEntry)
    && previewEntryBelongsToCacheKey(activeEntry, descriptorCacheKey);
}

export function isActivePreviewFrameVisible(
  entries: OperatingSystemCachedPreviewEntry[],
  activeCacheKey: string | null,
  descriptorCacheKey: string,
  rect: { width: number; height: number } | null,
  targetConnected: boolean,
  runtimeContentVisible = true,
): boolean {
  if (!runtimeContentVisible || !targetConnected || !rect || rect.width <= 160 || rect.height <= 120) return false;
  if (!isActivePreviewEntryLoaded(entries, activeCacheKey, descriptorCacheKey)) return false;
  const activeEntry = entries.find((entry) => entry.cacheKey === activeCacheKey);
  return !!activeEntry && shouldRenderPreviewFrame(activeEntry, entries);
}

export function refreshPreviewEntryDescriptorMetadata(
  entry: OperatingSystemCachedPreviewEntry,
  descriptor: OperatingSystemPreviewDescriptor,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry {
  if (!previewEntryBelongsToCacheKey(entry, descriptor.cacheKey) || entry.url !== descriptor.url) {
    return entry;
  }

  return {
    ...entry,
    ...descriptor,
    cacheKey: entry.cacheKey,
    candidateForCacheKey: entry.candidateForCacheKey,
    createdAt: entry.createdAt,
    lastUsedAt: now,
    loadedAt: entry.loadedAt,
    reloadNonce: entry.reloadNonce,
  };
}

export function rememberPreviewSrcDoc(
  entry: OperatingSystemCachedPreviewEntry,
  srcDoc: string,
): OperatingSystemCachedPreviewEntry {
  return { ...entry, lastSuccessfulSrcDoc: srcDoc };
}

export function clearRetainedPreviewSrcDoc(
  entry: OperatingSystemCachedPreviewEntry,
): OperatingSystemCachedPreviewEntry {
  return { ...entry, lastSuccessfulSrcDoc: null };
}

export function isPreviewEntrySupersededByDescriptor(
  entry: Pick<OperatingSystemCachedPreviewEntry, "osId" | "version">,
  descriptor: Pick<OperatingSystemPreviewDescriptor, "osId" | "version">,
): boolean {
  return entry.osId === descriptor.osId && entry.version !== descriptor.version;
}

export function activePreviewCacheKeyAfterCandidatePromotion(
  entries: Array<Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">>,
  activeCacheKey: string | null,
  parentCacheKey: string,
  promotedCacheKey: string,
): string | null {
  if (!activeCacheKey || activeCacheKey === promotedCacheKey) return activeCacheKey;
  if (activeCacheKey === parentCacheKey) return promotedCacheKey;
  const activeEntry = entries.find((entry) => entry.cacheKey === activeCacheKey);
  return activeEntry && previewEntryBelongsToCacheKey(activeEntry, parentCacheKey)
    ? promotedCacheKey
    : activeCacheKey;
}

export function shouldRenderPreviewFrame(
  entry: Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey">,
  entries: Array<Pick<OperatingSystemCachedPreviewEntry, "cacheKey" | "candidateForCacheKey" | "loadedAt" | "lastSuccessfulSrcDoc">>,
): boolean {
  if (entry.candidateForCacheKey) return true;
  return !entries.some((candidate) => (
    candidate.candidateForCacheKey === entry.cacheKey && shouldRevealPreviewFrame(candidate)
  ));
}

export function shouldRevealPreviewFrame(
  entry: Pick<OperatingSystemCachedPreviewEntry, "loadedAt" | "lastSuccessfulSrcDoc">,
): boolean {
  return !!entry.loadedAt
    && typeof entry.lastSuccessfulSrcDoc === "string"
    && entry.lastSuccessfulSrcDoc.trim().length > 0;
}

/**
 * A single blank heartbeat is not enough evidence that a live runtime was
 * discarded. Generated apps can briefly empty their root while routing or
 * refreshing data, so recovery waits for a sustained blank signal.
 */
export function shouldRecoverAfterRuntimeHeartbeatBlank(
  blankCount: number,
  blankSince: number | null,
  now: number,
  minimumSamples = 3,
  minimumDurationMs = 4_000,
): boolean {
  return blankSince !== null
    && blankCount >= minimumSamples
    && now - blankSince >= minimumDurationMs;
}

/**
 * Provider readiness is not browser readiness. Only reset the viewer's retry
 * budget after the iframe has actually reported usable content.
 */
export function shouldResetPreviewRefreshAttempts(
  providerStatus: unknown,
  previewLoaded: boolean,
): boolean {
  return providerStatus === "ready" && previewLoaded;
}

// Root HTML can be ready while its modules still return 503 during startup.
// Retry the same ticket before the slower ticket recovery takes over.
export function runtimeStartupErrorRetryDelay(attempt: number): number | null {
  return [2_000, 5_000, 10_000][attempt] ?? null;
}

export function hidePreviewEntryAfterRuntimeError(
  entry: OperatingSystemCachedPreviewEntry,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry {
  return { ...entry, loadedAt: null, lastUsedAt: now };
}

export function recoverPreviewEntryAfterRuntimeError(
  entry: OperatingSystemCachedPreviewEntry,
  retryCount: number,
  maxReloads: number,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry {
  if (shouldRevealPreviewFrame(entry)) {
    return {
      ...entry,
      loadedAt: null,
      lastUsedAt: now,
      reloadNonce: retryCount < maxReloads
        ? entry.reloadNonce + 1
        : entry.reloadNonce,
    };
  }

  if (entry.loadedAt && entry.candidateForCacheKey) {
    return {
      ...entry,
      lastUsedAt: now,
    };
  }

  return {
    ...entry,
    loadedAt: null,
    lastUsedAt: now,
    reloadNonce: retryCount < maxReloads
      ? entry.reloadNonce + 1
      : entry.reloadNonce,
  };
}

/**
 * Hide a runtime that lost rendered content while retaining its last good
 * document in memory. The page overlay stays visible until a new iframe
 * content signal proves recovery.
 */
export function recoverPreviewEntryAfterHeartbeat(
  entry: OperatingSystemCachedPreviewEntry,
  retryCount: number,
  maxReloads: number,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry {
  if (!shouldRevealPreviewFrame(entry)) {
    return recoverPreviewEntryAfterRuntimeError(entry, retryCount, maxReloads, now);
  }

  return {
    ...entry,
    loadedAt: null,
    lastUsedAt: now,
    reloadNonce: retryCount < maxReloads
      ? entry.reloadNonce + 1
      : entry.reloadNonce,
  };
}

type RuntimeLifecycleEventSource = Pick<
  OperatingSystemPreviewDescriptor,
  "cacheKey" | "osId" | "runtimeSource"
> & {
  candidateForCacheKey?: string | null;
};

function stableRuntimeUrlKey(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function createNextCachedPreviewEntry(
  descriptor: OperatingSystemPreviewDescriptor,
  existingEntry: OperatingSystemCachedPreviewEntry | null | undefined,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry {
  const runtimeUrlChanged = !!existingEntry && existingEntry.url !== descriptor.url;
  const keepVisibleLoadedRuntime = runtimeUrlChanged
    && !!existingEntry
    && shouldRevealPreviewFrame(existingEntry);

  if (keepVisibleLoadedRuntime) {
    return {
      ...descriptor,
      url: existingEntry.url,
      embedUrl: existingEntry.embedUrl,
      proxyExpiresAt: existingEntry.proxyExpiresAt,
      createdAt: existingEntry.createdAt,
      lastUsedAt: now,
      loadedAt: existingEntry.loadedAt,
      reloadNonce: existingEntry.reloadNonce,
      lastSuccessfulSrcDoc: existingEntry.lastSuccessfulSrcDoc ?? null,
    };
  }

  return {
    ...descriptor,
    createdAt: existingEntry?.createdAt ?? now,
    lastUsedAt: now,
    loadedAt: runtimeUrlChanged ? null : existingEntry?.loadedAt ?? null,
    reloadNonce: runtimeUrlChanged ? (existingEntry?.reloadNonce ?? 0) + 1 : existingEntry?.reloadNonce ?? 0,
    lastSuccessfulSrcDoc: runtimeUrlChanged ? null : existingEntry?.lastSuccessfulSrcDoc ?? null,
  };
}

export function createCandidateCachedPreviewEntry(
  descriptor: OperatingSystemPreviewDescriptor,
  existingEntry: OperatingSystemCachedPreviewEntry | null | undefined,
  now = Date.now(),
): OperatingSystemCachedPreviewEntry | null {
  const runtimeUrlChanged = !!existingEntry && existingEntry.url !== descriptor.url;
  if (!runtimeUrlChanged || !existingEntry || !shouldRevealPreviewFrame(existingEntry)) return null;

  return {
    ...descriptor,
    cacheKey: `${descriptor.cacheKey}:candidate:${stableRuntimeUrlKey(descriptor.url)}`,
    candidateForCacheKey: descriptor.cacheKey,
    createdAt: now,
    lastUsedAt: now,
    loadedAt: null,
    reloadNonce: (existingEntry.reloadNonce ?? 0) + 1,
    lastSuccessfulSrcDoc: null,
  };
}

export function buildOperatingSystemRuntimeLifecycleEvent(
  source: RuntimeLifecycleEventSource,
  signal: OperatingSystemRuntimeLifecycleClientSignal,
  reason: OperatingSystemRuntimeLifecycleClientReason,
  phase?: OperatingSystemRuntimeLifecycleClientPhase | null,
): OperatingSystemRuntimeLifecycleEventPayload {
  return {
    operatingSystemId: source.osId,
    signal,
    runtimeSource: source.runtimeSource,
    version: 1,
    cacheKey: source.candidateForCacheKey || source.cacheKey,
    reason,
    ...(phase ? { phase } : {}),
  };
}

export function operatingSystemRuntimeLifecycleEventKey(
  payload: OperatingSystemRuntimeLifecycleEventPayload,
): string {
  return [
    payload.operatingSystemId,
    payload.cacheKey,
    payload.signal,
    payload.reason,
    payload.phase ?? "",
  ].join(":");
}
